import { useState } from "react";
import { Alert, Box } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useUploadStoryImageMutation } from "Api/Mutations/Stories/useUploadStoryImageMutation";
import {
  MediaUploadField,
  type MediaUploadIssue,
  getFileNameFromUrl,
  getMediaUploadIssue,
} from "Components/Stories/MediaUploadField";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_FILE_SIZE_MB,
} from "Components/Stories/storyMediaLimits";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Stories.ImageUpload;

type Props = {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
};

/** Drag-and-drop / click-to-select image upload; uploads through our API to our blob storage. */
export const ImageUploadField = ({ label, hint, value, onChange }: Props) => {
  const { t } = useResource();
  // The picked file's name, kept while this field owns the value; null after a reload or failure.
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [validationIssue, setValidationIssue] =
    useState<MediaUploadIssue | null>(null);

  const {
    mutate: uploadImage,
    isPending: isUploading,
    isError,
  } = useUploadStoryImageMutation({
    onSuccess: onChange,
    onError: () => setUploadedFileName(null),
  });

  const validateAndUpload = (file: File) => {
    const issue = getMediaUploadIssue(
      file,
      ACCEPTED_IMAGE_TYPES,
      MAX_IMAGE_FILE_SIZE_MB,
    );
    setValidationIssue(issue);
    if (issue === null) {
      setUploadedFileName(file.name);
      uploadImage(file);
    }
  };

  return (
    <MediaUploadField
      label={label}
      hint={hint}
      acceptedTypes={ACCEPTED_IMAGE_TYPES}
      isBusy={isUploading}
      preview={
        value ? (
          <Box
            component="img"
            src={value}
            alt={label}
            width="100%"
            height="100%"
            sx={{ objectFit: "contain", display: "block" }}
          />
        ) : null
      }
      fileName={
        value ? (uploadedFileName ?? getFileNameFromUrl(value)) : undefined
      }
      emptyIcon={<CloudUploadIcon color="action" />}
      promptText={t(COMPONENT_RESOURCES.Prompt)}
      alerts={
        <>
          {validationIssue === "invalidType" && (
            <Alert severity="warning">
              {t(COMPONENT_RESOURCES.InvalidType)}
            </Alert>
          )}
          {validationIssue === "tooLarge" && (
            <Alert severity="warning">
              {t(COMPONENT_RESOURCES.TooLarge, {
                megabytes: MAX_IMAGE_FILE_SIZE_MB,
              })}
            </Alert>
          )}
          {isError && (
            <Alert severity="error">{t(COMPONENT_RESOURCES.Error)}</Alert>
          )}
        </>
      }
      onFile={validateAndUpload}
      onClear={() => {
        setUploadedFileName(null);
        onChange("");
      }}
    />
  );
};
